import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type SelectChangeEvent } from '@mui/material/Select';
import { type AllTextDefaultStylesType } from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import { SecondaryMenuItemTitle, SelectInputTwoCol } from '../../component';
import { useTranslation } from 'react-i18next';
import FontSizeInput from '../../component/FontSizeInput';
import TextStyleButtons from '../../component/TextStyleButtons';
import TextAlignmentButtons from '../../component/TextAlignmentButtons';
import Grid from '@mui/material/Grid';
import { useFormatStyleFieldOptions } from './useFormatStyleFieldOptions';
import { type TableRowStyleType } from 'components/editor/grid/reduxStore/tableRowDefaultStyleSlice';
import { ROW_BG_COLOR_ATTRIBUTE_NAME } from './FormatSettings';
import { ColorPickerWithUndoRedoSection } from '../../component/ColorPickerWithundoRedoSection';

export type DefaultStylePropertiesProps = {
  textStyle: AllTextDefaultStylesType;
  onTextStyleChange: (attributeName: string, value: string) => void;
  rowStyle?: TableRowStyleType;
  onRowStyleChange?: (value: string) => void;
};

export function DefaultStyleProperties({ textStyle, onTextStyleChange, rowStyle, onRowStyleChange }: DefaultStylePropertiesProps) {
  const { t } = useTranslation();
  const { fontSizeOptions, lineHeightMenuItems, lineHeightMenuItemsVal, allAvailableFonts, getLineHeightDisplayName } =
    useFormatStyleFieldOptions();

  const handleTextDefaultStyleChange = (styleToUpdate: string, value: string) => onTextStyleChange(styleToUpdate, value);

  const handleRowStyleChange = (value: string) => onRowStyleChange?.(value);

  const handleFontFamilyChange = (event: SelectChangeEvent<string[]>) => {
    const next = event.target.value[0];
    if (!next) return;
    onTextStyleChange('fontFamily', next);
  };

  const handleLineHeightChange = (event: SelectChangeEvent<string[]>) => {
    const name = event.target.value[0];
    if (!name) return;
    handleTextDefaultStyleChange('lineHeight', lineHeightMenuItemsVal[name]);
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" fontWeight={600}>
        {t('document.grid.default_styles.properties')}
      </Typography>
      <Stack spacing={1.5}>
        <SelectInputTwoCol
          label={t('document.grid.default_styles.font')}
          testId="format-font-family"
          menuItems={allAvailableFonts}
          value={textStyle.fontFamily ? [textStyle.fontFamily as string] : []}
          onChange={handleFontFamilyChange}
          variant="outlined"
          maxWidth="180px"
          customSelectStyle={{ padding: '8px 12px' }}
        />
        <Grid container alignItems="center" justifyContent="space-between" gap={1} data-testid="format-font-size">
          <SecondaryMenuItemTitle>{t('document.grid.default_styles.font_size')}</SecondaryMenuItemTitle>
          <FontSizeInput
            key={`font-size-${textStyle.fontSize}`}
            width="64px"
            value={textStyle.fontSize as string}
            onChange={(value) => handleTextDefaultStyleChange('fontSize', value)}
            menuItems={fontSizeOptions}
            variant="outlined"
          />
        </Grid>
        <ColorPickerWithUndoRedoSection
          title={t('document.grid.default_styles.color')}
          currentColor={textStyle.color as string}
          onColorChange={(color) => handleTextDefaultStyleChange('color', color)}
        />
        <SelectInputTwoCol
          label={t('document.grid.default_styles.line_spacing')}
          testId="format-line-spacing"
          menuItems={lineHeightMenuItems}
          value={[getLineHeightDisplayName(textStyle.lineHeight)]}
          onChange={handleLineHeightChange}
          variant="outlined"
          customSelectStyle={{ padding: '8px 12px' }}
        />
        <Grid container alignItems="center" justifyContent="space-between" gap={1} data-testid="format-text-style">
          <Grid item>
            <SecondaryMenuItemTitle>{t('document.grid.default_styles.text_style')}</SecondaryMenuItemTitle>
          </Grid>
          <Grid item>
            <TextStyleButtons
              key={`text-style-${textStyle.fontWeight}`}
              onChange={(attributeName, value) => handleTextDefaultStyleChange(attributeName, value)}
              textDefaultStyles={textStyle}
            />
          </Grid>
        </Grid>
        <Grid container alignItems="center" justifyContent="space-between" gap={1}>
          <Grid item>
            <SecondaryMenuItemTitle>{t('document.grid.default_styles.text_alignment')}</SecondaryMenuItemTitle>
          </Grid>
          <Grid item>
            <TextAlignmentButtons
              key={`text-alignment-${textStyle.textAlign}`}
              onChange={(alignment) => handleTextDefaultStyleChange('textAlign', alignment)}
              activeAlignment={textStyle.textAlign}
            />
          </Grid>
        </Grid>
        {rowStyle && onRowStyleChange && (
          <ColorPickerWithUndoRedoSection
            title={t('document.grid.default_styles.row_body_background_color')}
            currentColor={rowStyle[ROW_BG_COLOR_ATTRIBUTE_NAME]}
            onColorChange={handleRowStyleChange}
          />
        )}
      </Stack>
    </Stack>
  );
}
